import React, { useState, useEffect, useRef } from 'react';
import { BarChart3, Download, Calendar, TrendingUp, Users, DollarSign, Loader2, ChevronDown, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';

type PeriodType = '1m' | '3m' | '6m' | '12m' | 'custom';

const Relatorios: React.FC = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState<PeriodType>('6m');
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const periodModalRef = useRef<HTMLDivElement>(null);
  
  const [metrics, setMetrics] = useState({
    receitaTotal: 0,
    totalAlunos: 0,
    taxaOcupacao: 0,
    aulasRealizadas: 0,
    variacaoReceita: 0,
    variacaoAlunos: 0,
    variacaoOcupacao: 0,
    variacaoAulas: 0
  });
  const [revenueData, setRevenueData] = useState<Array<{ month: string; revenue: number; students: number }>>([]);
  const [levelDistribution, setLevelDistribution] = useState<Array<{ name: string; value: number; color: string }>>([]);

  useEffect(() => {
    if (userProfile?.empresaCnpj) {
      loadRelatoriosData();
    }
  }, [userProfile?.empresaCnpj, periodType, customStartDate, customEndDate]);

  // Fechar modal ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (periodModalRef.current && !periodModalRef.current.contains(event.target as Node)) {
        setShowPeriodModal(false);
      }
    };

    if (showPeriodModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPeriodModal]);

  const getPeriodDates = (): { startDate: Date; endDate: Date } => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    let startDate = new Date();

    if (periodType === 'custom') {
      if (customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        return { startDate, endDate: end };
      }
      // Se não houver datas customizadas, usar 6 meses como padrão
      startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    }

    switch (periodType) {
      case '1m':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case '3m':
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case '6m':
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case '12m':
        startDate.setMonth(startDate.getMonth() - 12);
        break;
    }
    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  };

  const getPeriodLabel = (): string => {
    if (periodType === 'custom') {
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate).toLocaleDateString('pt-BR');
        const end = new Date(customEndDate).toLocaleDateString('pt-BR');
        return `${start} - ${end}`;
      }
      return 'Personalizado';
    }
    
    const labels: Record<PeriodType, string> = {
      '1m': 'Último mês',
      '3m': 'Últimos 3 meses',
      '6m': 'Últimos 6 meses',
      '12m': 'Últimos 12 meses',
      'custom': 'Personalizado'
    };
    return labels[periodType];
  };

  const loadRelatoriosData = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      const { startDate, endDate } = getPeriodDates();
      const data = await firebaseService.getRelatoriosData(
        userProfile.empresaCnpj,
        startDate,
        endDate
      );
      
      setMetrics(data.metrics);
      setRevenueData(data.revenueData);
      setLevelDistribution(data.levelDistribution);
    } catch (error) {
      console.error('Erro ao carregar dados de relatórios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: PeriodType) => {
    setPeriodType(period);
    if (period !== 'custom') {
      setShowPeriodModal(false);
    }
  };

  const handleCustomPeriodApply = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      if (start <= end) {
        setShowPeriodModal(false);
      } else {
        alert('A data inicial deve ser anterior à data final');
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Relatórios</h1>
          <p className="text-gray-600 dark:text-gray-400">Análises e insights do seu negócio</p>
        </div>
        <div className="flex items-center gap-x-4">
          <div className="relative" ref={periodModalRef}>
            <button 
              onClick={() => setShowPeriodModal(!showPeriodModal)}
              className="flex items-center gap-x-2 px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <Calendar className="h-4 w-4" />
              <span>{getPeriodLabel()}</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {showPeriodModal && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Selecionar Período</h3>
                    <button
                      onClick={() => setShowPeriodModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => handlePeriodChange('1m')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        periodType === '1m'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Último mês
                    </button>
                    <button
                      onClick={() => handlePeriodChange('3m')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        periodType === '3m'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Últimos 3 meses
                    </button>
                    <button
                      onClick={() => handlePeriodChange('6m')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        periodType === '6m'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Últimos 6 meses
                    </button>
                    <button
                      onClick={() => handlePeriodChange('12m')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        periodType === '12m'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Últimos 12 meses
                    </button>
                    <button
                      onClick={() => handlePeriodChange('custom')}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        periodType === 'custom'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      Personalizado
                    </button>
                  </div>

                  {periodType === 'custom' && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Data Inicial
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Data Final
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            min={customStartDate}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                        <button
                          onClick={handleCustomPeriodApply}
                          disabled={!customStartDate || !customEndDate}
                          className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button className="flex items-center gap-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
            <Download className="h-4 w-4" />
            Exportar
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <DollarSign className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Receita Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(metrics.receitaTotal)}</p>
              <p className={`text-sm flex items-center ${metrics.variacaoReceita >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <TrendingUp className={`h-4 w-4 mr-1 ${metrics.variacaoReceita < 0 ? 'rotate-180' : ''}`} />
                {metrics.variacaoReceita >= 0 ? '+' : ''}{metrics.variacaoReceita.toFixed(1)}% vs mês anterior
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-success-100 dark:bg-success-900/30 rounded-lg">
              <Users className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total de Alunos</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{metrics.totalAlunos}</p>
              <p className={`text-sm flex items-center ${metrics.variacaoAlunos >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <TrendingUp className={`h-4 w-4 mr-1 ${metrics.variacaoAlunos < 0 ? 'rotate-180' : ''}`} />
                {metrics.variacaoAlunos >= 0 ? '+' : ''}{metrics.variacaoAlunos.toFixed(1)}% vs mês anterior
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
              <BarChart3 className="h-6 w-6 text-warning-600 dark:text-warning-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Taxa de Ocupação</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{metrics.taxaOcupacao}%</p>
              <p className={`text-sm flex items-center ${metrics.variacaoOcupacao >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <TrendingUp className={`h-4 w-4 mr-1 ${metrics.variacaoOcupacao < 0 ? 'rotate-180' : ''}`} />
                {metrics.variacaoOcupacao >= 0 ? '+' : ''}{metrics.variacaoOcupacao.toFixed(1)}% vs mês anterior
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg">
              <Calendar className="h-6 w-6 text-secondary-600 dark:text-secondary-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aulas Realizadas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{metrics.aulasRealizadas.toLocaleString('pt-BR')}</p>
              <p className={`text-sm flex items-center ${metrics.variacaoAulas >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                <TrendingUp className={`h-4 w-4 mr-1 ${metrics.variacaoAulas < 0 ? 'rotate-180' : ''}`} />
                {metrics.variacaoAulas >= 0 ? '+' : ''}{metrics.variacaoAulas.toFixed(1)}% vs mês anterior
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Receita Mensal
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
              <XAxis dataKey="month" stroke="#6b7280" className="dark:stroke-gray-400" />
              <YAxis stroke="#6b7280" className="dark:stroke-gray-400" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--bg-color, white)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Students Growth */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Crescimento de Alunos
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
              <XAxis dataKey="month" stroke="#6b7280" className="dark:stroke-gray-400" />
              <YAxis stroke="#6b7280" className="dark:stroke-gray-400" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--bg-color, white)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="students" stroke="#22c55e" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Level Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Distribuição por Nível
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={levelDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {levelDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--bg-color, white)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Relatórios Detalhados
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Relatório de Alunos</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Lista completa de alunos com informações detalhadas</p>
            <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm">Gerar Relatório</button>
          </div>
          
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Relatório Financeiro</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Análise completa da situação financeira</p>
            <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm">Gerar Relatório</button>
          </div>
          
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Relatório de Turmas</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Ocupação e performance das turmas</p>
            <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm">Gerar Relatório</button>
          </div>
          
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Relatório de Professores</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Performance e carga horária dos professores</p>
            <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm">Gerar Relatório</button>
          </div>
          
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Relatório de Inadimplência</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Análise de pagamentos em atraso</p>
            <button className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-sm">Gerar Relatório</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Relatorios;
